import {NteScope, nteSource} from 'app/components/OrderImageryModal/orderImageryUtils';
import {
  CartGroupBy,
  CartItemV1,
  cartTotalAcres,
  cartTotalCents,
  orderedItemPriceCents,
  prepaidRemainingCents,
} from 'app/modules/Cart';
import {
  DisplayGroup,
  GroupMeasure,
  bucketDisplayGroups,
  propertyGroupKey,
} from 'app/modules/Cart/grouping';
import type {ApiImageryBudget} from 'app/modules/Remote/api';
import type {ApiImageryPrepaid} from 'app/modules/Remote/Organization';
import type {ApiImageryContract} from 'app/modules/Remote/Project';
import * as CONSTANTS from 'app/utils/constants';
import {formatCost} from 'app/utils/costUtils';

import type {OrderedLine} from './types';

export type GroupNteStatus =
  | {kind: 'loading'}
  | {kind: 'noContract'}
  | {kind: 'noCap'}
  | {kind: 'withinCap'; scope: NteScope; remainingCents: number}
  | {kind: 'overCap'; scope: NteScope; remainingCents: number; overageCents: number}
  | {kind: 'capExhausted'; scope: NteScope; overspentCents: number};

export interface ContractLookupEntry {
  contract: ApiImageryContract | null;
  isLoading: boolean;
}

/** An organization on budgets has one cap across every contract in the cart. */
export interface BudgetLookupEntry {
  budget: ApiImageryBudget | null;
  isLoading: boolean;
}

const NO_BUDGET: BudgetLookupEntry = {budget: null, isLoading: false};

export type OrderedDisplayGroup = DisplayGroup<OrderedLine>;

export interface CartContractGroup {
  key: string;
  label: string;
  projectNames: string[];
  contract: ApiImageryContract | null;
  items: CartItemV1[];
  subtotalCents: number;
  acres: number;
  nte: GroupNteStatus;
}

/**
 * How much of a not-to-exceed cap a subtotal would consume. The cap is the organization's budget
 * when it has one, and otherwise the contract's; only dollar-unit contracts carry a cents cap. The
 * acre-unit branch survives in the type so org settings can render expired contracts — none are
 * active — so it counts as uncapped here, the same case `OrderImageryModal` narrows away before it
 * checks a limit at all.
 *
 * An organization that pays at checkout needs no contract, and only its budget can cap it.
 */
export function groupNteStatus(
  contract: ApiImageryContract | null,
  isLoading: boolean,
  subtotalCents: number,
  budget: BudgetLookupEntry = NO_BUDGET,
  paysAtCheckout = false
): GroupNteStatus {
  if (contract === null && !paysAtCheckout) {
    return isLoading ? {kind: 'loading'} : {kind: 'noContract'};
  }
  if (budget.isLoading) {
    return {kind: 'loading'};
  }
  const nte = nteSource(
    contract?.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT ? contract.usage : null,
    budget.budget
  );
  if (nte === null || nte.nteCents === null) {
    return {kind: 'noCap'};
  }
  const {scope, nteCents, spentCents} = nte;
  const remainingCents = nteCents - spentCents;
  // A cap with nothing left can't take any order, no matter the subtotal, so treat it as a
  // whole-group failure rather than an overage the cart could shed by removing items.
  if (remainingCents <= 0) {
    return {kind: 'capExhausted', scope, overspentCents: spentCents - nteCents};
  }
  const overageCents = subtotalCents - remainingCents;
  return overageCents > 0
    ? {kind: 'overCap', scope, remainingCents, overageCents}
    : {kind: 'withinCap', scope, remainingCents};
}

export const portfolioNames = (group: CartContractGroup) => group.projectNames.join(', ');

/**
 * Why a cap blocks Submit, or null when none does. Customers know their portfolios, not the
 * contracts behind them, so a contract's block names portfolios; a budget is the organization's, so
 * its block names the organization.
 */
export function nteBlockMessage(groups: CartContractGroup[]): string | null {
  for (const group of groups) {
    if (group.nte.kind === 'capExhausted') {
      return group.nte.scope === 'organization'
        ? 'No funds remaining for your organization. Have an account administrator contact us for options.'
        : `No funds remaining for ${portfolioNames(group)}. Remove those scenes, or have an account administrator contact us for options.`;
    }
  }
  for (const group of groups) {
    if (group.nte.kind === 'overCap') {
      return group.nte.scope === 'organization'
        ? `Remove ${formatCost(group.nte.overageCents)} of imagery to fit within your organization’s remaining funds.`
        : `Remove ${formatCost(group.nte.overageCents)} of imagery from ${portfolioNames(group)} to fit within the funds remaining.`;
    }
  }
  return null;
}

/** A spending limit or prepaid the cart draws on, and how much of it was used before this order. */
export interface UsageLine {
  key: string;
  label: string;
  /** The portfolios it belongs to, named once the cart bills more than one group. */
  portfolios: string | null;
  usedCents: number;
  totalCents: number;
  /** ISO8601. When a spending limit starts counting purchases: its budget term's or contract's start. */
  since: string | null;
}

/**
 * The spending limits and prepaids the cart draws on, as they stand before this order. A budget is
 * one limit across every contract, so it's listed once. A contract's limit and prepaid are its own,
 * so they're listed per contract, named by the portfolios it bills.
 */
export function usageAndLimits(
  groups: CartContractGroup[],
  budget: ApiImageryBudget | null,
  prepaids: ApiImageryPrepaid[] | null
): UsageLine[] {
  const lines: UsageLine[] = [];
  let listedBudget = false;
  for (const group of groups) {
    if (group.nte.kind === 'loading' || group.nte.kind === 'noContract') {
      continue;
    }
    const portfolios = groups.length > 1 ? portfolioNames(group) : null;
    const usage =
      group.contract?.contractType === CONSTANTS.CONTRACT_TYPE_DOLLAR_UNIT
        ? group.contract.usage
        : null;
    const nte = nteSource(usage, budget);
    if (nte?.nteCents != null && !(nte.scope === 'organization' && listedBudget)) {
      const isBudget = nte.scope === 'organization';
      listedBudget ||= isBudget;
      lines.push({
        key: isBudget ? 'limit-organization' : `limit-${group.key}`,
        label: 'Spending limit',
        portfolios: isBudget ? null : portfolios,
        usedCents: nte.spentCents,
        totalCents: nte.nteCents,
        since: isBudget ? (budget?.startDate ?? null) : (group.contract?.startDate ?? null),
      });
    }
    if (usage && usage.prepaid_cents > 0) {
      lines.push({
        key: `prepaid-${group.key}`,
        label: 'Prepaid',
        portfolios,
        // Purchases past the prepaid go on the invoice, not against it.
        usedCents: Math.min(usage.purchased_cents, usage.prepaid_cents),
        totalCents: usage.prepaid_cents,
        since: null,
      });
    }
  }
  if (prepaids && prepaids.length > 0) {
    const totalCents = prepaids.reduce((total, prepaid) => total + prepaid.cents, 0);
    lines.push({
      key: 'prepaid-credit',
      label: 'Prepaid credit',
      portfolios: null,
      usedCents: totalCents - prepaidRemainingCents(prepaids),
      totalCents,
      since: null,
    });
  }
  return lines;
}

/** (nte − spent) − the subtotal; null when no cap applies. */
export const remainingAfterCents = (nte: GroupNteStatus, subtotalCents: number): number | null =>
  nte.kind === 'withinCap' || nte.kind === 'overCap' ? nte.remainingCents - subtotalCents : null;

/**
 * Splits the cart the way the endpoint bills it. An invoiced organization gets one group per
 * imagery contract, since a checkout spanning two contracts is refused outright; projects sharing a
 * contract share a group. An organization that pays at checkout bills no contract, so its whole
 * cart is one group and one charge.
 *
 * A budget caps the organization's spend across contracts, so under one every group is measured
 * against the whole cart's subtotal: groups that each fit can still overrun it together.
 */
export function groupCartItemsByContract(
  items: CartItemV1[],
  contracts: ReadonlyMap<string, ContractLookupEntry>,
  budget: BudgetLookupEntry = NO_BUDGET,
  paysAtCheckout = false
): CartContractGroup[] {
  const order: string[] = [];
  const draft = new Map<
    string,
    {contract: ApiImageryContract | null; isLoading: boolean; items: CartItemV1[]; names: string[]}
  >();

  const classify = (item: CartItemV1) => {
    if (paysAtCheckout) {
      return {key: 'organization', contract: null, isLoading: false};
    }
    const entry = contracts.get(item.projectId);
    const contract = entry?.contract ?? null;
    // Without a contract there's nothing to bill against yet, so those items group by project and
    // say so rather than pooling into one meaningless bucket.
    return {
      key: contract ? `contract-${contract.id}` : `project-${item.projectId}`,
      contract,
      isLoading: entry?.isLoading ?? true,
    };
  };

  for (const item of items) {
    const {key, contract, isLoading} = classify(item);

    let group = draft.get(key);
    if (!group) {
      group = {contract, isLoading, items: [], names: []};
      draft.set(key, group);
      order.push(key);
    }
    group.items.push(item);
    if (!group.names.includes(item.snapshot.projectName)) {
      group.names.push(item.snapshot.projectName);
    }
  }

  const billableCents = cartTotalCents(
    [...draft.values()].flatMap((group) => (group.contract || paysAtCheckout ? group.items : []))
  );

  return order.map((key) => {
    const group = draft.get(key)!;
    const subtotalCents = cartTotalCents(group.items);
    const cappedCents = budget.budget ? billableCents : subtotalCents;
    return {
      key,
      label: group.contract?.name ?? group.names.join(', '),
      projectNames: group.names,
      contract: group.contract,
      items: group.items,
      subtotalCents,
      acres: cartTotalAcres(group.items),
      nte: groupNteStatus(group.contract, group.isLoading, cappedCents, budget, paysAtCheckout),
    };
  });
}

/** What the server billed, never the cart's quote: a submit-time re-quote makes the two differ. */
const ORDERED_MEASURE = {
  sortKey: (line: OrderedLine) => line.ordered.sensingTime,
  cents: (line: OrderedLine) => orderedItemPriceCents(line.ordered),
  acres: (line: OrderedLine) => line.ordered.billedAcres,
} satisfies Omit<GroupMeasure<OrderedLine>, 'classify'>;

/** The property and portfolio views of a placed order; a receipt has no source view. */
export type OrderedGroupBy = Extract<CartGroupBy, 'property' | 'portfolio'>;

/**
 * An unpaired line buckets on its own rather than pooling: without its cart row there's no
 * collection id, and a feature id alone doesn't identify a property. Keyed by purchase id, which
 * is unique per line.
 */
const orderedPropertyKey = ({item, ordered}: OrderedLine): string =>
  item ? propertyGroupKey(item) : `property-unmatched-${ordered.purchaseId}`;

const orderedFeatureLabel = ({item, ordered}: OrderedLine): string =>
  item?.snapshot.featureName ?? `Feature ${ordered.featureId}`;

/** Distinct properties an order covers. */
export const orderedPropertyCount = (lines: OrderedLine[]): number =>
  new Set(lines.map(orderedPropertyKey)).size;

/**
 * A placed order's scenes, grouped the way the cart groups its rows. Subtotals come from what the
 * server billed rather than the quote each row carried in.
 */
export function groupOrderedLines(
  lines: OrderedLine[],
  groupBy: OrderedGroupBy
): OrderedDisplayGroup[] {
  switch (groupBy) {
    case 'property':
      return bucketDisplayGroups(lines, {
        ...ORDERED_MEASURE,
        classify: (line) => ({
          key: orderedPropertyKey(line),
          label: orderedFeatureLabel(line),
          sublabel: line.item?.snapshot.projectName ?? null,
        }),
      });
    case 'portfolio':
      return bucketDisplayGroups(lines, {
        ...ORDERED_MEASURE,
        // The contract a group bills to isn't reachable here: a successful checkout empties the
        // cart, and the contract lookup is keyed off what's in it.
        classify: (line) => ({
          key: line.item
            ? `portfolio-${line.item.projectId}`
            : `portfolio-unmatched-${line.ordered.purchaseId}`,
          label: line.item?.snapshot.projectName ?? orderedFeatureLabel(line),
          sublabel: null,
        }),
      });
  }
}
